import { createContext, useContext, useMemo, type PropsWithChildren } from 'react';

export interface RepositoryToken<Repository> {
  readonly key: string;
  readonly _type?: Repository;
}

export type RepositoryRegistration = readonly [RepositoryToken<unknown>, unknown];
export type RepositoryValues =
  | Readonly<Record<string, unknown>>
  | ReadonlyMap<RepositoryToken<unknown>, unknown>
  | readonly RepositoryRegistration[];

const RepositoryContext = createContext<ReadonlyMap<string, unknown> | null>(null);

export function createRepositoryToken<Repository>(key: string): RepositoryToken<Repository> {
  if (key.trim().length === 0) throw new Error('Repository token key must not be empty');
  return { key };
}

export interface RepositoryProviderProps extends PropsWithChildren {
  readonly repositories?: RepositoryValues;
}

export function RepositoryProvider({ children, repositories = {} }: RepositoryProviderProps) {
  const values = useMemo(() => toRepositoryMap(repositories), [repositories]);
  return <RepositoryContext.Provider value={values}>{children}</RepositoryContext.Provider>;
}

export function useRepository<Repository>(token: RepositoryToken<Repository>): Repository {
  const repositories = useContext(RepositoryContext);
  if (repositories === null || !repositories.has(token.key)) {
    throw new Error(
      `Repository "${token.key}" is not configured. Wrap this feature route in RepositoryProvider.`,
    );
  }
  return repositories.get(token.key) as Repository;
}

export function useOptionalRepository<Repository>(
  token: RepositoryToken<Repository>,
): Repository | null {
  const repositories = useContext(RepositoryContext);
  if (repositories === null || !repositories.has(token.key)) return null;
  return repositories.get(token.key) as Repository;
}

function toRepositoryMap(repositories: RepositoryValues): ReadonlyMap<string, unknown> {
  const values = new Map<string, unknown>();
  const entries = Array.isArray(repositories)
    ? repositories.map(([token, value]) => [token.key, value] as const)
    : repositories instanceof Map
      ? [...repositories.entries()].map(([token, value]) => [token.key, value] as const)
      : Object.entries(repositories);

  for (const [key, value] of entries) {
    if (key.trim().length === 0) throw new Error('Repository token key must not be empty');
    if (values.has(key)) throw new Error(`Duplicate repository token key "${key}"`);
    values.set(key, value);
  }
  return values;
}
